import axios from 'axios';
import { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { LifeBuoy, CalendarDays } from 'lucide-react';
import { BlocChantiers } from '@/components/TableauBordTerrain';

const API_URL = (typeof window !== 'undefined' ? window.__API_URL__ : process.env.REACT_APP_BACKEND_URL) || '';

/**
 * 🆕 2026-05-07 — « BTs en renfort » pour les rôles bureau (CP, adjoint admin).
 *
 * Affiché dans Mon Espace UNIQUEMENT lorsque l'utilisateur a au moins 1 BT
 * où il est cédulé en renfort (membre d'équipe, pas chef). Réutilise le même
 * composant `BlocChantiers` que le tableau de bord terrain pour avoir un
 * comportement identique côté actions (puncher, ouvrir le BT, etc.).
 */
export function BTsEnRenfort({ token, role }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API_URL}/api/employees/mes-bts-renfort`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!cancelled && res.status === 200) setData(res.data);
      } catch (e) {
        // Silencieux : l'utilisateur ne devrait juste rien voir si l'endpoint plante
        console.error('Erreur BTsEnRenfort:', e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  if (loading) {
    return (
      <Card className="border-2 border-amber-100 bg-gradient-to-br from-amber-50 to-slate-50">
        <CardContent className="py-5 flex items-center gap-3">
          <div className="animate-spin w-5 h-5 border-2 border-amber-400 border-t-transparent rounded-full" />
          <span className="text-slate-500 text-sm">Chargement de tes BTs en renfort…</span>
        </CardContent>
      </Card>
    );
  }

  // 🎯 Discrétion : si la CP n'a aucun BT en renfort, on ne montre RIEN
  // (pas de carte vide qui pollue l'écran).
  if (!data) return null;
  const { bons_aujourd_hui = [], bons_prochains = [], today } = data;
  const hasToday = bons_aujourd_hui.length > 0;
  const hasProchains = bons_prochains.length > 0;
  if (!hasToday && !hasProchains) return null;

  return (
    <div className="space-y-3" data-testid="section-bts-en-renfort">
      {/* En-tête de section pour clarifier le contexte */}
      <div className="flex items-center gap-2 px-1 pt-1">
        <LifeBuoy className="w-4 h-4 text-amber-600" />
        <h3 className="text-sm font-semibold text-amber-900">
          BTs en renfort
        </h3>
        <span className="text-xs text-slate-400">
          (chantiers où tu es assigné·e ponctuellement)
        </span>
      </div>

      {hasToday && (
        <BlocChantiers
          titre="Mon BT en renfort aujourd'hui"
          sousTitre={`${bons_aujourd_hui.length} bon${bons_aujourd_hui.length > 1 ? 's' : ''} de travail`}
          bons={bons_aujourd_hui}
          isToday
          today={today}
          role={role}
          couleurIcone="text-amber-600"
          couleurBg="bg-amber-50"
          couleurPastille="bg-amber-500 animate-pulse"
          defaultOpen
        />
      )}

      {hasProchains && (
        <BlocChantiers
          titre="Mes prochains BTs en renfort"
          sousTitre={`${bons_prochains.length} bon${bons_prochains.length > 1 ? 's' : ''} prévu${bons_prochains.length > 1 ? 's' : ''}`}
          bons={bons_prochains}
          isToday={false}
          today={today}
          role={role}
          couleurIcone="text-amber-600"
          couleurBg="bg-amber-50/60"
          couleurPastille="bg-amber-300"
          defaultOpen={!hasToday}
        />
      )}
    </div>
  );
}
